import { resolveAssetFolderSlug } from '@core/utils/assetFolderSlug';
import { getGameFromUrl } from '@core/utils/urlParser';
import { Assets, Texture } from 'pixi.js';
import { Spine } from '@esotericsoftware/spine-pixi-v8';
// ADD imports
import { Spritesheet } from 'pixi.js';

export interface GameAssetManifest {
  version: string;
  assets: {
    images?: Record<string, string>;
    sounds?: Record<string, string>;
    spine?: Record<
      string,
      {
        skeleton: string;
        atlas: string;
        scale?: number;
      }
    >;
    atlases?: Record<
      string,
      {
        json: string;
        image: string;
      }
    >;
  };
}

export interface LoadOptions {
  preload?: boolean;
  cache?: boolean;
  scale?: number;
  assetSubDir?: string | false;
}

export class AssetService {
  private static instance: AssetService;
  private baseUrl: string;
  private loadedAssets = new Map<string, any>();
  private initialized = false;

  private constructor(baseUrl: string = '') {
    this.baseUrl = baseUrl;
  }

  private resolveGameAssetBase(gameSlug: string, options: LoadOptions): string {
    if (options.assetSubDir === false) {
      return `${this.baseUrl}/games/${gameSlug}`;
    }

    if (typeof options.assetSubDir === 'string') {
      const normalized = options.assetSubDir.replace(/^\/+/, '');
      return `${this.baseUrl}/games/${gameSlug}/${normalized}`;
    }

    const routeSlug = getGameFromUrl();
    if (routeSlug) {
      const gameAssetFolder = resolveAssetFolderSlug(gameSlug);
      const routeAssetFolder = resolveAssetFolderSlug(routeSlug);

      // Nested folder only when the route maps to a different public asset folder
      // within the same game root (e.g. crash + sky-sniffer → games/crash/skysniffer).
      // Aliases like sky-x → skyx_new must not append /skyx.
      if (routeAssetFolder && routeAssetFolder !== gameAssetFolder) {
        const urlAssetFolder = routeSlug.replace(/-/g, '');
        return `${this.baseUrl}/games/${gameSlug}/${urlAssetFolder}`;
      }
    }

    return `${this.baseUrl}/games/${gameSlug}`;
  }

  static getInstance(baseUrl?: string): AssetService {
    if (!AssetService.instance) {
      AssetService.instance = new AssetService(baseUrl);
    }
    return AssetService.instance;
  }

  /**
   * Initialize the asset service with a game's manifest
   */
  async initializeGame(
    gameSlug: string,
    _manifest?: GameAssetManifest
  ): Promise<void> {
    if (this.initialized) return;

    // const manifestPath = `${this.baseUrl}/games/${gameSlug}/manifest.json`;
    const basePath = `${this.baseUrl}/games/${gameSlug}`;

    try {
      // Initialize PIXI Assets without manifest (we handle loading manually)
      await Assets.init({
        basePath: basePath,
        texturePreference: {
          resolution: 1,
          format: ['png', 'jpg', 'jpeg', 'webp'],
        },
      });

      console.log(`[AssetService] Initialized for game: ${gameSlug}`);
      this.initialized = true;
    } catch (error) {
      console.error(`[AssetService] Failed to initialize:`, error);
      throw error;
    }
  }

  // ADD below loadTexture()
  async loadAtlas(
    gameSlug: string,
    atlasName: string,
    jsonPath: string,
    imagePath: string,
    options: LoadOptions = {}
  ): Promise<Spritesheet> {
    const cacheKey = `atlas:${gameSlug}:${atlasName}`;

    if (options.cache !== false && this.loadedAssets.has(cacheKey)) {
      return this.loadedAssets.get(cacheKey);
    }

    // 🔥 SAME LOGIC AS SPINE
    const base = this.resolveGameAssetBase(gameSlug, options);

    const imageUrl = `${base}/${imagePath}`;
    const jsonUrl = `${base}/${jsonPath}`;

    try {
      const texture = await Assets.load<Texture>(imageUrl);
      const atlasData = await fetch(jsonUrl).then(r => {
        if (!r.ok) {
          throw new Error(`Failed to load atlas json: ${jsonUrl}`);
        }
        return r.json();
      });

      const sheet = new Spritesheet(texture, atlasData);
      await sheet.parse();

      if (options.cache !== false) {
        this.loadedAssets.set(cacheKey, sheet);
      }

      console.log(
        `[AssetService] Loaded atlas '${atlasName}' for game '${gameSlug}'`
      );

      return sheet;
    } catch (error) {
      console.error(
        `[AssetService] Failed to load atlas '${atlasName}'`,
        error
      );
      throw error;
    }
  }

  /**
   * Load a texture/image asset
   */
  async loadTexture(
    gameSlug: string,
    assetName: string,
    options: LoadOptions = {}
  ): Promise<Texture> {
    const assetPath = `${this.baseUrl}/games/${gameSlug}/${assetName}`;
    const cacheKey = `texture:${gameSlug}:${assetName}`;

    if (options.cache !== false && this.loadedAssets.has(cacheKey)) {
      return this.loadedAssets.get(cacheKey);
    }

    try {
      const texture = await Assets.load(assetPath);
      if (options.cache !== false) {
        this.loadedAssets.set(cacheKey, texture);
      }
      return texture;
    } catch (error) {
      console.error(
        `[AssetService] Failed to load texture ${assetName}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Ensure skeleton + atlas are registered/loaded in PIXI Assets.
   * Display objects must not be cached — callers may destroy them (e.g. responsive reload).
   */
  private async ensureSpineAssetsLoaded(
    gameSlug: string,
    spineName: string,
    options: LoadOptions = {}
  ): Promise<{ skeletonAlias: string; atlasAlias: string }> {
    const spineBasePath = this.resolveGameAssetBase(gameSlug, options);
    const skeletonPath = `${spineBasePath}/spine/${spineName}.json`;
    const atlasPath = `${spineBasePath}/spine/${spineName}.atlas`;

    const safeSpineName = spineName.replace(/[\\/]/g, '-');
    const skeletonAlias = `${gameSlug}-${safeSpineName}-skeleton`;
    const atlasAlias = `${gameSlug}-${safeSpineName}-atlas`;

    // PIXI Assets caches by alias; only register once.
    if (!Assets.get(skeletonAlias)) {
      Assets.add({ alias: skeletonAlias, src: skeletonPath });
    }
    if (!Assets.get(atlasAlias)) {
      Assets.add({ alias: atlasAlias, src: atlasPath });
    }

    await Assets.load([skeletonAlias, atlasAlias]);
    return { skeletonAlias, atlasAlias };
  }

  /**
   * Load a Spine animation.
   * Always returns a fresh Spine display object. Skeleton/atlas stay in PIXI Assets cache.
   */
  async loadSpine(
    gameSlug: string,
    spineName: string,
    options: LoadOptions = {}
  ): Promise<Spine> {
    try {
      const { skeletonAlias, atlasAlias } = await this.ensureSpineAssetsLoaded(
        gameSlug,
        spineName,
        options
      );

      // Never cache the Spine instance — destroying a cached instance leaves a dead
      // object in loadedAssets (skyx_new responsive jet ↔ jet_mobile swap on resize).
      const spine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
        scale: options.scale || 1.0,
      });

      console.log(
        `[AssetService] Loaded spine ${spineName} for game ${gameSlug}`
      );
      return spine;
    } catch (error) {
      console.error(`[AssetService] Failed to load spine ${spineName}:`, error);
      throw error;
    }
  }

  /**
   * Drop a stale spine display-object entry if one was cached by older code paths.
   */
  clearSpineInstance(gameSlug: string, spineName: string): void {
    const cacheKey = `spine:${gameSlug}:${spineName}`;
    if (this.loadedAssets.has(cacheKey)) {
      this.loadedAssets.delete(cacheKey);
    }
  }

  /**
   * Load an audio asset
   */
  async loadAudio(
    gameSlug: string,
    audioName: string,
    options: LoadOptions = {}
  ): Promise<HTMLAudioElement> {
    const cacheKey = `audio:${gameSlug}:${audioName}`;

    if (options.cache !== false && this.loadedAssets.has(cacheKey)) {
      return this.loadedAssets.get(cacheKey);
    }

    return new Promise((resolve, reject) => {
      const audio = new Audio();
      audio.preload = 'auto';
      audio.src = `${this.baseUrl}/games/${gameSlug}/sounds/${audioName}`;

      const cleanup = () => {
        audio.removeEventListener('canplaythrough', onCanPlay);
        audio.removeEventListener('error', onError);
      };

      const onCanPlay = () => {
        cleanup();
        if (options.cache !== false) {
          this.loadedAssets.set(cacheKey, audio);
        }
        resolve(audio);
      };

      const onError = (_e: any) => {
        cleanup();
        reject(new Error(`Failed to load audio: ${audioName}`));
      };

      audio.addEventListener('canplaythrough', onCanPlay, { once: true });
      audio.addEventListener('error', onError, { once: true });
      audio.load();
    });
  }

  /**
   * Preload all assets for a game
   */
  async preloadGameAssets(
    gameSlug: string,
    manifest: GameAssetManifest
  ): Promise<void> {
    const promises: Promise<any>[] = [];

    // Preload images
    if (manifest.assets.images) {
      for (const [name, path] of Object.entries(manifest.assets.images)) {
        promises.push(
          this.loadTexture(gameSlug, path, { cache: true }).catch(error => {
            console.warn(
              `[AssetService] Failed to preload image ${name}:`,
              error
            );
            return null;
          })
        );
      }
    }

    // Preload spine skeleton/atlas only (do not create display objects)
    if (manifest.assets.spine) {
      for (const [name] of Object.entries(manifest.assets.spine)) {
        promises.push(
          this.ensureSpineAssetsLoaded(gameSlug, name).catch(error => {
            console.warn(
              `[AssetService] Failed to preload spine ${name}:`,
              error
            );
            return null;
          })
        );
      }
    }

    // Preload sounds
    if (manifest.assets.sounds) {
      for (const [name, path] of Object.entries(manifest.assets.sounds)) {
        promises.push(
          this.loadAudio(gameSlug, path, { cache: true }).catch(error => {
            console.warn(
              `[AssetService] Failed to preload audio ${name}:`,
              error
            );
            return null;
          })
        );
      }
    }

    await Promise.allSettled(promises);
    console.log(`[AssetService] Preloaded assets for game ${gameSlug}`);
  }

  /**
   * Get a cached asset
   */
  getAsset<T = any>(
    gameSlug: string,
    assetType: 'texture' | 'spine' | 'audio',
    assetName: string
  ): T | null {
    const cacheKey = `${assetType}:${gameSlug}:${assetName}`;
    return this.loadedAssets.get(cacheKey) || null;
  }

  /**
   * Clear cached assets for a specific game
   */
  clearGameAssets(gameSlug: string): void {
    const keysToDelete: string[] = [];
    for (const key of this.loadedAssets.keys()) {
      if (key.includes(`:${gameSlug}:`)) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => this.loadedAssets.delete(key));
    console.log(`[AssetService] Cleared assets for game ${gameSlug}`);
  }

  /**
   * Clear all cached assets
   */
  clearAllAssets(): void {
    this.loadedAssets.clear();
    console.log(`[AssetService] Cleared all cached assets`);
  }

  /**
   * Get available animations for a spine object
   */
  getSpineAnimations(spine: Spine): string[] {
    return spine?.skeleton?.data?.animations?.map(a => a.name) ?? [];
  }

  /**
   * Set animation on a spine object
   */
  setSpineAnimation(
    spine: Spine,
    animationName: string,
    loop: boolean = true
  ): void {
    if (!spine) return;
    const exists = !!spine.skeleton.data.findAnimation(animationName);
    if (exists) {
      spine.state.setAnimation(0, animationName, loop);
    } else {
      console.warn(`[AssetService] Animation "${animationName}" not found`);
    }
  }
}
